// ============================================================================
// Componente: AutomacaoBeneficioSocial.js
// Objetivo: Interface para extração da gestão de Benefício Social (RH).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    ArrowLeft, Play, CheckCircle, AlertCircle, 
    Loader2, FileSpreadsheet, Database, Clock, HelpCircle
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoBeneficioSocial = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    // Estados da Automação
    const [isProcessing, setIsProcessing] = useState(false);
    const [status, setStatus] = useState('idle'); 
    const [executionId, setExecutionId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null); // NOVO ESTADO
    
    // Estados de Ajuda
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Estados de Progresso
    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando inicialização...');

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    // Estilos Auxiliares
    const corModulo = '#e11d48'; 

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // NOVO: Buscar a última data de modificação ao carregar a página
    useEffect(() => {
        const fetchUltimaAtualizacao = async () => {
            try {
                const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                const response = await fetch('/api/automacao-rh/rh-ultima-atualizacao-confere', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.ultimaData) {
                        const dataObj = new Date(data.ultimaData);
                        // Formata para o padrão brasileiro: DD/MM/YYYY HH:MM
                        const formatada = dataObj.toLocaleString('pt-BR', { 
                            day: '2-digit', month: '2-digit', year: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                        });
                        setUltimaAtualizacao(formatada);
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar a data de atualização:", error);
            }
        };

        fetchUltimaAtualizacao();
    }, []);

    // Polling para buscar o progresso em tempo real
    useEffect(() => {
        let interval;
        if (isProcessing && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    
                    const response = await fetch(`/api/automacao-rh/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        if (data.progress !== undefined) setProgress(data.progress);
                        
                        if (data.message) {
                            setStatusText(data.message);
                        } else if (data.logs && data.logs.length > 0) {
                            setStatusText(data.logs[data.logs.length - 1]);
                        }
                    }
                } catch (error) {
                    console.error("Erro ao buscar progresso:", error);
                }
            }, 1500);
        }
        return () => clearInterval(interval);
    }, [isProcessing, executionId]);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    const resetState = () => {
        setStatus('idle');
        setErrorMessage('');
        setProgress(0);
        setStatusText('Aguardando inicialização...');
    };

    const iniciarAutomacao = async () => {
        resetState();
        const newExecutionId = Date.now().toString();
        setExecutionId(newExecutionId);
        setIsProcessing(true);
        setStatus('processing');
        setProgress(5);
        setStatusText("Conectando ao banco de dados...");
        setErrorMessage('');

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            
            const response = await fetch(`/api/automacao-rh/rh-beneficio-social`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ executionId: newExecutionId })
            });

            if (response.ok) {
                setProgress(100);
                setStatusText("Extração concluída. Iniciando download do ZIP...");
                
                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                
                const disposition = response.headers.get('Content-Disposition');
                let fileName = 'Relatorios_Beneficio_Social.zip';
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                    if (matches != null && matches[1]) fileName = matches[1].replace(/['"]/g, '');
                }
                
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                
                setStatus('success');
                setStatusText("Download concluído com sucesso!");
            } else {
                const errorData = await response.json();
                setStatus('error');
                setErrorMessage(errorData.error || 'Ocorreu um erro ao extrair os dados do banco.');
                setStatusText(`Falha: ${errorData.error || 'Erro desconhecido'}`);
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            setStatus('error');
            setErrorMessage('Erro de conexão com o servidor. Verifique sua internet ou tente novamente.');
            setStatusText("Falha de comunicação com o servidor.");
        } finally {
            setIsProcessing(false);
        }
    };

    const getProgressBarColor = () => {
        if (status === 'error') return '#ef4444'; 
        if (status === 'success') return '#10b981'; 
        return '#e11d48'; 
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>

                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FileSpreadsheet size={24} color="#e11d48" /> Benefício Social
                        </h2>
                        <button 
                            type="button"
                            onClick={() => setIsHelpOpen(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#fff1f2', color: corModulo, border: '1px solid #fecdd3', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                        </button>
                        </div>
                    </div>
                    
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-5xl mx-auto">
                    
                    {/* CARTÃO PRINCIPAL DE AÇÃO */}
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Extração de Dados</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
                            Esta automação conecta-se diretamente ao banco de dados para extrair os registros de funcionários ativos. 
                            O sistema irá gerar planilhas no formato Excel legado (.xls) separadas automaticamente por <strong>Centro de Custo</strong> e disponibilizá-las em um único arquivo compactado (.zip).
                        </p>

                        <div style={{ 
                            backgroundColor: '#f8fafc', border: '1px solid #f0e2e2', borderRadius: '12px', 
                            padding: '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', 
                            gap: '16px', marginBottom: '24px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ backgroundColor: '#fee0e0', padding: '16px', borderRadius: '50%', color: '#c70202' }}>
                                    <Database size={28} />
                                </div>
                                <div>
                                    <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#553333' }}>Consulta em Tempo Real</h4>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#8b6464' }}>
                                        Não é necessário anexar planilhas. Os dados são lidos da dtbConfere.
                                    </p>
                                </div>
                            </div>

                            {/* Exibição da Última Data */}
                            {ultimaAtualizacao ? (
                                <div style={{ 
                                    textAlign: isMobile ? 'left' : 'right', backgroundColor: '#fff', padding: '10px 16px', 
                                    borderRadius: '8px', border: '1px solid #f0e2e2', display: 'flex', flexDirection: 'column', gap: '4px',
                                    minWidth: isMobile ? '100%' : 'auto'
                                }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#b89494', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: '6px' }}>
                                        <Clock size={12} /> Última Atualização no Banco
                                    </span>
                                    <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                        {ultimaAtualizacao}
                                    </span>
                                </div>
                            ) : (
                                <div style={{ fontSize: '12px', color: '#b89494' }}>Carregando status da base...</div>
                            )}
                        </div>

                        {errorMessage && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: '500' }}>
                                <AlertCircle size={18} /> {errorMessage}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={iniciarAutomacao}
                                disabled={isProcessing}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', 
                                    backgroundColor: isProcessing ? '#e1cbcb' : '#e11d48', 
                                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', 
                                    fontSize: '15px', cursor: isProcessing ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.2)',
                                    width: isMobile ? '100%' : 'auto', justifyContent: 'center'
                                }}
                            >
                                {isProcessing ? (
                                    <><Loader2 size={18} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} /> Extraindo dados...</>
                                ) : (
                                    <><Play size={18} /> Iniciar Extração</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* CARD DE PROGRESSO */}
                    {status !== 'idle' && (
                        <div style={{ 
                            backgroundColor: '#fff', 
                            borderRadius: '16px', 
                            padding: '24px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {status === 'processing' && <Loader2 size={24} color="#0ea5e9" className="animate-spin" />}
                                    {status === 'success' && <CheckCircle size={24} color="#10b981" />}
                                    {status === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ 
                                width: '100%', height: '12px', backgroundColor: '#f1f5f9', 
                                borderRadius: '99px', overflow: 'hidden'
                            }}>
                                <div style={{ 
                                    width: `${progress}%`, height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>
            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA VALE TRANSPORTE */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="beneficio-social" 
                showOnlyIds={['beneficio-social']} 
            />
        </div>
    );
};

export default AutomacaoBeneficioSocial;